import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface SplashScreenProps {
  onFinish: () => void;
}

const SplashScreen = ({ onFinish }: SplashScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Force finish after 5 seconds even if progress is stuck
    const forceFinishTimeout = setTimeout(() => {
      console.log('Force finishing splash screen after timeout');
      onFinish();
    }, 5000);

    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            console.log('Splash screen progress complete');
            onFinish();
          }, 500);
          return 100;
        }
        return prevProgress + 4;
      });
    }, 100);

    return () => {
      clearInterval(interval);
      clearTimeout(forceFinishTimeout);
    };
  }, [onFinish]);

  const handleImageError = () => {
    console.error('Failed to load logo image');
    setError('Failed to load logo');
  };

  return (
    <motion.div 
      className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-r from-blue-600 to-cyan-400 z-50"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center"
      >
        {/* Logo */}
        <motion.div
          className="h-32 w-32 mb-8 flex items-center justify-center"
          animate={{ 
            y: [0, -10, 0],
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 2,
            ease: "easeInOut" 
          }}
        >
          <img
            src="/uploads/logo.png"
            alt="DG IT Logo"
            className="h-full w-auto"
            onError={handleImageError}
          />
        </motion.div>
        
        {/* Company Name */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-4xl font-bold text-white mb-2"
        >
          DG IT <span className="text-xl">Pvt. Ltd.</span>
        </motion.div>
        
        {/* Slogan */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-white text-lg mb-8"
        >
          Creative Digital Agency
        </motion.div>

        {/* Progress bar */}
        <div className="w-64 bg-white/20 rounded-full h-2 mb-4 overflow-hidden">
          <motion.div 
            className="h-full bg-white rounded-full"
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>

        {/* Loading dots */}
        <div className="flex space-x-2">
          <motion.div
            className="w-2 h-2 bg-white rounded-full"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity, repeatType: "loop" }}
          />
          <motion.div
            className="w-2 h-2 bg-white rounded-full"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity, repeatType: "loop", delay: 0.2 }}
          />
          <motion.div
            className="w-2 h-2 bg-white rounded-full"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity, repeatType: "loop", delay: 0.4 }}
          />
        </div>

        {/* Error message */}
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-200 mt-4"
          >
            {error}
          </motion.div>
        )}

        {/* Skip button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          whileHover={{ opacity: 1 }}
          className="mt-8 text-white text-sm underline"
          onClick={onFinish}
        >
          Skip
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default SplashScreen;
